import type { SchemaElementDecl, SchemaFacets, SchemaModel, SchemaTypeDef } from './types';

export function schemaModelForShort(
  models: Record<string, SchemaModel>,
  short: string,
): SchemaModel | undefined {
  return models[short];
}

/** Resolve a type, following complexContent extension bases once. */
export function resolveType(model: SchemaModel, typeName: string): SchemaTypeDef | undefined {
  const seen = new Set<string>();
  let name: string | undefined = typeName;
  let merged: SchemaTypeDef | undefined;

  while (name && !seen.has(name)) {
    seen.add(name);
    const t: SchemaTypeDef | undefined = model.types[name];
    if (!t) break;
    if (!merged) {
      merged = { ...t, children: t.children ? [...t.children] : undefined };
    } else {
      if (t.children?.length) {
        merged.children = [...(merged.children ?? []), ...t.children];
      }
      if (t.choice?.length) {
        merged.choice = [...(merged.choice ?? []), ...t.choice];
      }
      if (t.attributes?.length) {
        merged.attributes = [...(merged.attributes ?? []), ...t.attributes];
      }
      if (t.facets && !merged.facets) merged.facets = t.facets;
      if (t.documentation && !merged.documentation) merged.documentation = t.documentation;
      if (t.kind === 'simple' || t.kind === 'simpleContent') {
        merged.kind = t.kind;
        merged.contentType = t.contentType ?? merged.contentType;
      }
    }
    name = t.contentType;
  }
  return merged;
}

export function childDecls(model: SchemaModel, typeName: string): SchemaElementDecl[] {
  const t = resolveType(model, typeName);
  if (!t) return [];
  if (t.choice?.length) return t.choice;
  return t.children ?? [];
}

export function isRequired(decl: SchemaElementDecl): boolean {
  return decl.minOccurs >= 1;
}

export function occursLabel(decl: SchemaElementDecl): string {
  const max = decl.maxOccurs === 'unbounded' ? 'n' : String(decl.maxOccurs);
  return `${decl.minOccurs}..${max}`;
}

export function facetSummary(facets: SchemaFacets | undefined): string {
  if (!facets) return '';
  const parts: string[] = [];
  if (facets.base) parts.push(facets.base);
  if (facets.pattern) parts.push(`pattern ${facets.pattern}`);
  if (facets.enumeration?.length) {
    parts.push(
      facets.enumeration.length <= 8
        ? facets.enumeration.join(' | ')
        : `${facets.enumeration.slice(0, 6).join(' | ')}…`,
    );
  }
  if (facets.maxLength != null) parts.push(`maxLength ${facets.maxLength}`);
  if (facets.minLength != null) parts.push(`minLength ${facets.minLength}`);
  if (facets.length != null) parts.push(`length ${facets.length}`);
  return parts.join(' · ');
}

/** Help line for a declaration: Required/Optional · occurs · type facets. */
export function helpLine(model: SchemaModel, decl: SchemaElementDecl): string {
  const req = isRequired(decl) ? 'Required' : 'Optional';
  const t = resolveType(model, decl.typeName);
  const bits = [req, occursLabel(decl), decl.typeName];
  const facets = t?.facets ?? (t?.contentType ? resolveType(model, t.contentType)?.facets : undefined);
  const f = facetSummary(facets);
  if (f) bits.push(f);
  if (t?.documentation) bits.push(t.documentation);
  return bits.join(' · ');
}

/**
 * Given present child element names under a parent of `parentTypeName`,
 * return required missing decls and optional absent decls.
 */
export function missingChildren(
  model: SchemaModel,
  parentTypeName: string,
  presentNames: string[],
): { requiredMissing: SchemaElementDecl[]; optionalAbsent: SchemaElementDecl[] } {
  const decls = childDecls(model, parentTypeName);
  const present = new Set(presentNames);
  const requiredMissing: SchemaElementDecl[] = [];
  const optionalAbsent: SchemaElementDecl[] = [];
  for (const decl of decls) {
    if (present.has(decl.name)) continue;
    if (isRequired(decl)) requiredMissing.push(decl);
    else optionalAbsent.push(decl);
  }
  return { requiredMissing, optionalAbsent };
}

/** Map an XML path under Document (e.g. FIToFICstmrCdtTrf/GrpHdr) to the element's type name. */
export function typeNameAtPath(model: SchemaModel, path: string): string | undefined {
  const parts = path.split('/').filter(Boolean);
  if (parts.length === 0) return model.documentChild.typeName;
  if (parts[0] !== model.rootElement && parts[0] !== 'Document') {
    // Allow paths that omit Document but start at root message element
  }
  let start = 0;
  if (parts[0] === 'Document') start = 1;
  if (parts[start] !== model.rootElement) return undefined;

  let typeName = model.documentChild.typeName;
  for (let i = start + 1; i < parts.length; i++) {
    const name = parts[i];
    const decls = childDecls(model, typeName);
    const hit = decls.find((d) => d.name === name);
    if (!hit) return undefined;
    typeName = hit.typeName;
  }
  return typeName;
}
